import { useState, useMemo, useEffect } from 'react'
import { Columns3, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useColumnVisibilityStore } from '@/stores/useColumnVisibilityStore'
import type { DagColumnMeta } from '@/types/dag'

interface ColumnSelectorPopoverProps {
  columns: DagColumnMeta[]
  groups: string[]
}

export function ColumnSelectorPopover({
  columns,
  groups,
}: ColumnSelectorPopoverProps) {
  const [search, setSearch] = useState('')
  const [activeGroup, setActiveGroup] = useState<string | null>(null)
  const [showHiddenOnly, setShowHiddenOnly] = useState(false)

  const {
    hiddenColumns,
    toggleColumn,
    setColumnsHidden,
    removeStaleHiddenColumns,
    resetToDefaults,
    userHasCustomized,
  } = useColumnVisibilityStore()
  const activeGroups = useMemo(() => {
    const groupsWithColumns = new Set(columns.map(c => c.group))
    return groups.filter(g => groupsWithColumns.has(g))
  }, [columns, groups])

  const columnIds = useMemo(() => new Set(columns.map(c => c.id)), [columns])
  const hiddenCount = useMemo(
    () => [...hiddenColumns].filter(id => columnIds.has(id)).length,
    [hiddenColumns, columnIds]
  )

  // Clean up stale hidden column IDs without marking as user-customized
  useEffect(() => {
    if (columns.length === 0) return
    const staleIds = [...hiddenColumns].filter(id => !columnIds.has(id))
    if (staleIds.length > 0) {
      removeStaleHiddenColumns(staleIds)
    }
  }, [columns]) // eslint-disable-line react-hooks/exhaustive-deps

  // Filter columns by search and group
  const filteredColumns = useMemo(() => {
    let result = columns
    if (showHiddenOnly) {
      result = result.filter(c => hiddenColumns.has(c.id))
    }
    if (activeGroup) {
      result = result.filter(c => c.group === activeGroup)
    }
    if (search) {
      const q = search.toLowerCase()
      result = result.filter(c => c.label.toLowerCase().includes(q))
    }
    return result
  }, [columns, activeGroup, search, showHiddenOnly, hiddenColumns])

  // Group filtered columns by their group
  const groupedFiltered = useMemo(() => {
    const map = new Map<string, DagColumnMeta[]>()
    for (const col of filteredColumns) {
      const arr = map.get(col.group) ?? []
      arr.push(col)
      map.set(col.group, arr)
    }
    return map
  }, [filteredColumns])

  // Select all state for filtered columns
  const filteredIds = useMemo(
    () => filteredColumns.map(c => c.id),
    [filteredColumns]
  )
  const visibleFilteredCount = filteredIds.filter(
    id => !hiddenColumns.has(id)
  ).length
  const allChecked =
    filteredIds.length > 0 && visibleFilteredCount === filteredIds.length
  const someChecked =
    visibleFilteredCount > 0 && visibleFilteredCount < filteredIds.length

  const handleSelectAll = (checked: boolean) => {
    setColumnsHidden(filteredIds, !checked)
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="gap-2 cursor-pointer">
          <Columns3 className="h-4 w-4" />
          Columns
          {hiddenCount > 0 && (
            <>
              <span className="h-4 w-px bg-border" />
              <span className="text-xs">{hiddenCount} hidden</span>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        {/* Search */}
        <div className="p-3 pb-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search columns..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-8 h-8 text-sm"
            />
          </div>
        </div>

        {/* Group filter pills */}
        <div className="flex gap-1.5 px-3 pb-2 overflow-x-auto">
          <Button
            variant="outline"
            size="sm"
            className={`cursor-pointer shrink-0 h-7 text-xs ${!activeGroup ? 'bg-primary text-white hover:bg-primary/90 hover:text-white' : ''}`}
            onClick={() => {
              setActiveGroup(null)
              setShowHiddenOnly(false)
            }}
          >
            All
          </Button>
          {activeGroups.map(group => (
            <Button
              key={group}
              variant="outline"
              size="sm"
              className={`cursor-pointer shrink-0 h-7 text-xs ${activeGroup === group ? 'bg-primary text-white hover:bg-primary/90 hover:text-white' : ''}`}
              onClick={() => {
                setActiveGroup(activeGroup === group ? null : group)
                setShowHiddenOnly(false)
              }}
            >
              {group}
            </Button>
          ))}
        </div>

        {/* Select all */}
        <div
          className="flex items-center gap-2 px-3 py-2 border-y border-border cursor-pointer select-none"
          onClick={() => handleSelectAll(!allChecked)}
        >
          <span className="pointer-events-none">
            <Checkbox
              checked={allChecked}
              {...(someChecked ? { 'data-state': 'indeterminate' } : {})}
            />
          </span>
          <span className="text-sm font-medium">Select all</span>
        </div>

        {/* Column list */}
        <div className="max-h-[360px] overflow-y-auto py-1">
          {activeGroups
            .filter(g => groupedFiltered.has(g))
            .map(groupName => (
              <div key={groupName}>
                <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  {groupName}
                </div>
                {groupedFiltered.get(groupName)!.map(col => (
                  <div
                    key={col.id}
                    className="flex items-center gap-2 px-3 py-1.5 hover:bg-muted/50 cursor-pointer select-none"
                    onClick={() => toggleColumn(col.id)}
                  >
                    <span className="pointer-events-none">
                      <Checkbox checked={!hiddenColumns.has(col.id)} />
                    </span>
                    <span className="text-sm">{col.label}</span>
                  </div>
                ))}
              </div>
            ))}
          {filteredColumns.length === 0 && (
            <div className="px-3 py-4 text-sm text-muted-foreground text-center">
              No columns match your search
            </div>
          )}
        </div>

        {/* Footer actions */}
        {(hiddenCount > 0 || userHasCustomized) && (
          <div className="border-t border-border px-3 py-2 flex flex-col gap-1">
            {hiddenCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className={`cursor-pointer w-full h-7 text-xs ${showHiddenOnly ? 'bg-primary text-white hover:bg-primary/90 hover:text-white' : ''}`}
                onClick={() => {
                  setShowHiddenOnly(!showHiddenOnly)
                  setActiveGroup(null)
                }}
              >
                {showHiddenOnly
                  ? 'Show all columns'
                  : `Show hidden columns (${hiddenCount})`}
              </Button>
            )}
            {userHasCustomized && (
              <Button
                variant="ghost"
                size="sm"
                className="cursor-pointer w-full h-7 text-xs text-muted-foreground"
                onClick={() => resetToDefaults()}
              >
                Reset to defaults
              </Button>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
